import React, { useCallback, useEffect, useState } from 'react'
import {
  BrowserRouter, Redirect, Route, Switch,
} from 'react-router-dom'
import { IntlProvider } from 'react-intl'
import ptBR from 'date-fns/locale/pt-BR'
import { registerLocale, setDefaultLocale } from 'react-datepicker'
import UnlockPage from 'src/components/security/UnlockPage'
import Loading from 'src/components/Common/Loading'
import { ToastContainer } from 'react-toastify'
import APP_MESSAGES from './i18n/messages'
import TEMPLATE_MESSAGES from './i18n/templateMessages.json'
import { LANGUAGES } from './i18n/languages'
import Login from './features/login/login'
import Routes from './routes/Routes'
import {
  checkLoginByPass,
  handleLoginMode,
  setLoginMode,
} from './services/Security'
import { ssoClient, stockClient } from './services/SigninSetup'
import {
  checkFulfillmentCenterSelected,
  getFulfillmentCenterSelected,
} from './services/fulfillmentCenter/fulfillmentCenterService'
import './index.scss'
import { useDispatch } from 'react-redux'
import ContentLoading from './components/ContentLoading'
import { defaultFulfillmentCenterTypeFilters } from './features/fulfillmentCenterFilters'
import { actions } from './features/state'
import { unassignDock } from './services/docks/dockService'
import { googleLogout, GoogleOAuthProvider } from '@react-oauth/google';
import { clearPersistedSession } from './services/sessionCleanup'
import { buildLoginContinuePath } from './services/continueNavigation'

const MESSAGES = {
  'pt-BR': { ...TEMPLATE_MESSAGES['pt-BR'], ...APP_MESSAGES['pt-BR'] },
}

registerLocale('pt-BR', ptBR)
setDefaultLocale('pt-BR')

function Root() {
  const currentLocale = LANGUAGES[LANGUAGES.default].code

  const [loader, setLoader] = useState(true)
  const [locked, setLocked] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [bypassActive, setBypassActive] = useState(null)
  const [selectedFulfillmentCenter, setSelectedFulfillmentCenter] = useState(
    () => getFulfillmentCenterSelected() || null,
  )
  const dispatch = useDispatch()

  const handleFulfillmentCenterSelected = useCallback((fulfillmentCenter) => {
    setSelectedFulfillmentCenter(fulfillmentCenter || null)
  }, [])

  const handleFulfillmentCenterCleared = useCallback(() => {
    setSelectedFulfillmentCenter(null)
  }, [])

  const resetPersistedSession = useCallback((options = { preserveLocalStorage: true }) => {
    clearPersistedSession(options)
    dispatch(actions.setToken(undefined))
    dispatch(actions.clearFulfillmentCenters())
    setSelectedFulfillmentCenter(null)
    setCurrentUser(null)
    setLocked(false)
  }, [dispatch])

  const handleBypassAuthStateChange = useCallback(async (state) => {
    try {
      if (state === 'initialized' || state === 'logged_in') {
        const user = await stockClient.getUser()
        if (!user) {
          resetPersistedSession()
          return
        }

        setCurrentUser(user)
        setLocked(false)
        setLoginMode('bypass')
      } else if (state === 'logged_out') {
        resetPersistedSession()
      } else if (state === 'locked') {
        setLocked(true)
        setCurrentUser(null)
      } else {
        return
      }
    } catch (error) {
      console.error(error)
      setCurrentUser(null)
      setLocked(false)
    } finally {
      setLoader(false)
    }
  }, [resetPersistedSession])

  const handleGoogleAuthStateChange = useCallback(async (state) => {
    try {
      if (state === 'initialized' || state === 'logged_in') {
        const user = await ssoClient.getUser()
        if (!user) {
          resetPersistedSession()
          return
        }

        setCurrentUser(user)
        setLocked(false)
        setLoginMode('google')
      } else if (state === 'logged_out') {
        resetPersistedSession()
      } else if (state === 'locked') {
        setLocked(true)
        setCurrentUser(null)
      } else {
        return
      }
    } catch (error) {
      console.error(error)
      setCurrentUser(null)
      setLocked(false)
    } finally {
      setLoader(false)
    }
  }, [resetPersistedSession])

  const syncAuthBootstrap = useCallback(async (loginMode) => {
    if (loginMode === 'bypass') {
      await handleBypassAuthStateChange('initialized')
      return
    }

    await handleGoogleAuthStateChange('initialized')
  }, [handleBypassAuthStateChange, handleGoogleAuthStateChange])

  useEffect(() => {
    if (currentUser && selectedFulfillmentCenter) {
      dispatch(actions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
    }
  }, [currentUser, dispatch, selectedFulfillmentCenter?.id])

  useEffect(() => {
    checkLoginByPass()
      .then(async (data) => {
        const bypassActive = data.bypass_active
        setBypassActive(bypassActive)

        const loginMode = bypassActive ? 'bypass' : 'google'
        handleLoginMode(loginMode)

        if (bypassActive) {
          stockClient.onAuthStateChanged(handleBypassAuthStateChange)
        } else {
          ssoClient.onAuthStateChanged(handleGoogleAuthStateChange)
        }

        await syncAuthBootstrap(loginMode)
      })
      .catch((error) => {
        console.error(error)
        setCurrentUser(null)
        setLocked(false)
        setLoader(false)
      })
  }, [handleBypassAuthStateChange, handleGoogleAuthStateChange, syncAuthBootstrap])

  const onLogout = useCallback(
    async (e) => {
      if (e) e.preventDefault()

      if (bypassActive) {
        stockClient.handleLogout()
      } else {
        ssoClient.logout()
      }

      const dock = localStorage.currentDock && JSON.parse(localStorage.currentDock)
      !!dock && unassignDock(dock.id)

      resetPersistedSession({ preserveLocalStorage: false })
      googleLogout()
    },
    [bypassActive, resetPersistedSession],
  )

  function appLock(e) {
    e.preventDefault()

    if (bypassActive) {
      stockClient.lock()
    } else {
      ssoClient.lock()
    }
  }

  async function appUnlock() {
    setLoader(true)

    try {
      const unlocked = bypassActive
        ? await stockClient.unlock()
        : await ssoClient.unlock()

      if (unlocked) {
        let user = null

        if (bypassActive) {
          user = await stockClient.getUser()
          setCurrentUser(user)
        } else {
          user = await ssoClient.getUser()
          setCurrentUser(user)
        }
        setLocked(false)
        return
      }

      await resetPersistedSession({ preserveLocalStorage: false })
    } catch (error) {
      console.error(error)
      await resetPersistedSession({ preserveLocalStorage: true })
    } finally {
      setLoader(false)
    }
  }

  useEffect(() => {
    (async () => {
      if (currentUser && selectedFulfillmentCenter) {
        await checkFulfillmentCenterSelected(currentUser, onLogout)
      }
    })()
  }, [currentUser, onLogout, selectedFulfillmentCenter?.id])


  const verifyToken = async () => {
    const token = await ssoClient.getToken()
    if (token) {
      dispatch(actions.setToken(token))
    } else {
      resetPersistedSession()
    }
  }

  useEffect(() => {
    verifyToken()
  }, [])

  return (  
    <div className='App'>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <IntlProvider locale={currentLocale} messages={MESSAGES[currentLocale]}>
        {!loader ? (
          <BrowserRouter basename='/'>
            <Switch>
              <Route
                exact
                path={`${process.env.PUBLIC_URL}/login`}
                component={({ location }) => {
                  const qs = new URLSearchParams(location.search)
                  if (currentUser && selectedFulfillmentCenter) {
                    return <Redirect to={qs.get('continue') || '/'} />
                  }
                  return (
                    <Login
                      bypassActive={bypassActive}
                      currentUser={currentUser}
                      onFulfillmentCenterSelected={handleFulfillmentCenterSelected}
                    />
                  )
                }}
              />
              {currentUser && selectedFulfillmentCenter ? (
                <>
                  <Routes
                    user={currentUser}
                    onLock={appLock}
                    onLogout={onLogout}
                    onChangeFulfillmentCenter={handleFulfillmentCenterCleared}
                  />
                </>
              ) : (
                <>
                  {locked ? (
                    <UnlockPage onUnlock={appUnlock} />
                  ) : (
                    <Redirect
                      push
                      to={`${process.env.PUBLIC_URL}${buildLoginContinuePath()}`}
                    />
                  )}
                </>
              )}
            </Switch>
          </BrowserRouter>
        ) : (
          <Loading />
        )}
        <ContentLoading />
      </IntlProvider>
      <ToastContainer />
      </GoogleOAuthProvider>
    </div>
  )
}

export default Root
